import { useState } from "react";
import { evaluateFormula } from "@Features/portal-settings/components/PortalSetings/Configuration/FormsAndFields/FormTemplate/constant.helper";
import { SECTION_TYPE_TABLE_SECTION } from "@Helpers/constants";
import { TemplateQuestionField, TemplateSection } from "@Helpers/types/layout/types";
import { t } from "@i18n";
import {
  buildConcatenatedValue,
  formSectionType,
  getPermissions,
  TEMPLATE_SOURCE_MAIL_CREATION,
  TEMPLATE_SOURCE_MAIL_SIDEBAR,
  toFiniteNumber,
} from "./constants";

/**
 * Hint shown under formula/concatenate fields while their value is still
 * incomplete (evaluates to null). Shared by the web and mobile renderers.
 */
export const getCalculatedFieldHintText = () =>
  t("This value is calculated automatically once all required fields are filled");

/**
 * Build the numeric scope for a formula field.
 *
 * A number field contributes its raw numeric answer. A referenced *formula*
 * field no longer resolves to `null` — instead its own expression is evaluated
 * recursively (against its own `fieldsVariableMapper`) so formulas can reference
 * other formula fields. The `visiting` set guards against cyclic references: a
 * formula that (transitively) references itself resolves to `null` rather than
 * recursing forever.
 *
 * @param rootId          `_id` of the formula field being rendered; seeds the
 *                        cycle guard so a referenced formula pointing back at it
 *                        resolves to `null`.
 * @param mapper          `fieldsVariableMapper` of the formula being rendered
 *                        (referenced field `_id` → variable name).
 * @param questionById    All candidate questions keyed by `_id`.
 * @param getRawValue     Raw (pre-`toFiniteNumber`) answer value for a field id.
 */
export const buildFormulaScope = (
  rootId: string,
  mapper: Record<string, string>,
  questionById: Map<string, TemplateQuestionField>,
  getRawValue: (id: string) => any,
): Record<string, number> => {
  const resolveValue = (question: TemplateQuestionField, visiting: Set<string>): number | null => {
    if (question.fieldType === "formula") {
      if (visiting.has(question._id)) return null;
      visiting.add(question._id);
      const nestedScope: Record<string, number> = {};
      Object.entries(question.fieldsVariableMapper ?? {}).forEach(([refId, varName]) => {
        const refQuestion = questionById.get(refId);
        if (!refQuestion || !varName) return;
        const value = resolveValue(refQuestion, visiting);
        if (value !== null) nestedScope[varName] = value;
      });
      visiting.delete(question._id);
      return evaluateFormula(question.formulaExpression || "", nestedScope);
    }
    return toFiniteNumber(getRawValue(question._id));
  };

  const scope: Record<string, number> = {};
  Object.entries(mapper).forEach(([refId, varName]) => {
    const question = questionById.get(refId);
    if (!question || !varName) return;
    const value = resolveValue(question, new Set([rootId]));
    if (value !== null) scope[varName] = value;
  });
  return scope;
};

export const collectNonTableQuestions = (template: TemplateSection[] = []): TemplateQuestionField[] =>
  template.flatMap((section) =>
    section.sectionCode === SECTION_TYPE_TABLE_SECTION ? [] : (section.questionFields ?? []),
  );

export const questionMapById = (questions: TemplateQuestionField[]): Map<string, TemplateQuestionField> =>
  new Map(questions.map((question) => [question._id, question]));

// Builds the numeric scope for a formula field (row-scoped for table sections,
// form-wide otherwise) and evaluates its expression. Referenced formula fields
// resolve recursively via buildFormulaScope. Returns null while any referenced
// field is still empty/non-numeric.
export const getFormulaFieldValue = ({ eachField = {}, section = {}, tableRowIndex = 0, formData = {} }: any) => {
  const fieldVariableMapper = eachField.fieldsVariableMapper ?? {};
  const formulaExpression = eachField.formulaExpression || "";

  let scope: Record<string, number>;
  if (section.sectionCode === SECTION_TYPE_TABLE_SECTION) {
    const rowData = section.tableDataSource?.[tableRowIndex] ?? {};
    const questionById = questionMapById(section.questionFields ?? []);
    scope = buildFormulaScope(eachField._id, fieldVariableMapper, questionById, (id) => rowData?.[id]?.value);
  } else {
    const questionById = questionMapById(collectNonTableQuestions(formData?.selectedForm?.template));
    scope = buildFormulaScope(
      eachField._id,
      fieldVariableMapper,
      questionById,
      (id) => questionById.get(id)?.answer?.value,
    );
  }

  return evaluateFormula(formulaExpression, scope);
};

// Resolves the values of the fields referenced by a concatenate field
// (row-scoped for table sections, form-wide otherwise) and joins them with the
// configured separator. Returns null while nothing resolves.
export const getConcatenateFieldValue = ({ eachField = {}, section = {}, tableRowIndex = 0, formData = {} }: any) => {
  const valueById = new Map<string, string>();

  if (section.sectionCode === SECTION_TYPE_TABLE_SECTION) {
    const rowData = section.tableDataSource?.[tableRowIndex] ?? {};
    Object.keys(rowData).forEach((key: string) => {
      valueById.set(key, rowData[key]?.value ?? "");
    });
  } else {
    (formData?.selectedForm?.template ?? []).forEach((templateSection: any) => {
      if (templateSection.sectionCode === SECTION_TYPE_TABLE_SECTION) return;
      (templateSection.questionFields ?? []).forEach((question: any) => {
        valueById.set(question._id, question?.answer?.value ?? "");
      });
    });
  }

  return buildConcatenatedValue(eachField.selectedFields, valueById, eachField.seperator) || null;
};

function useQuestionFields({
  eachField = {},
  tableRowId = "",
  extraParams = {},
  section = {},
  tableRowIndex = 0,
  source = "",
  formData = {},
  userId = "",
  disabled = false,
  showAnswersOnly = false,
  templateSource = "FORM_FILLING",
}: any) {
  const questionId = eachField._id ?? "";

  const [openUrlPopUpState, setOpenUrlPopUpState] = useState({
    state: false,
    url: "",
  });
  let name = tableRowId ? `${tableRowId}${questionId}` : questionId;
  if (extraParams.id !== undefined && extraParams.id !== null) {
    name = `${extraParams.id}_${name}`;
  }

  const sectionId = section._id ?? "";

  const { permissions, tableDataSource = [] } = section;

  let currentAnswer = eachField.answer ?? {};
  if (source === formSectionType.TABLE_SECTION) {
    currentAnswer = tableDataSource[tableRowIndex]?.[questionId] ?? {};
  }
  const extraCheck = eachField.allowPredefinedVal || eachField.allowPreDefinedColValues;
  const allowEditDuringFormFilling =
    !extraCheck || (extraCheck && (eachField.allowEditingOfPredefinedVal || section.allowEditPreDefineRowInForm));

  const isCurrentUserAllowedToFill = permissions
    ? getPermissions({ inspectionData: formData, permissions, type: "Fill", userId })
    : !disabled || templateSource === "TEMPLATE_CREATION";

  const isCurrentUserAllowedToModify = permissions
    ? getPermissions({ inspectionData: formData, permissions, type: "Modify", userId })
    : !disabled || templateSource === "TEMPLATE_CREATION";

  const enabled =
    (isCurrentUserAllowedToFill || isCurrentUserAllowedToModify) &&
    !showAnswersOnly &&
    ((["FORM_FILLING", TEMPLATE_SOURCE_MAIL_CREATION, TEMPLATE_SOURCE_MAIL_SIDEBAR].includes(templateSource) &&
      !!allowEditDuringFormFilling) ||
      (templateSource === "TEMPLATE_CREATION" && !!extraCheck));

  const formulaValue =
    eachField.fieldType === "formula" ? getFormulaFieldValue({ eachField, section, tableRowIndex, formData }) : null;

  const concatenatedValue =
    eachField.fieldType === "concatenate"
      ? getConcatenateFieldValue({ eachField, section, tableRowIndex, formData })
      : null;

  const autoNumberValue = currentAnswer?.value ?? "";

  return {
    questionId,
    name,
    sectionId,
    currentAnswer,
    isCurrentUserAllowedToFill,
    isCurrentUserAllowedToModify,
    enabled,
    permissions,
    setOpenUrlPopUpState,
    openUrlPopUpState,
    formulaValue,
    concatenatedValue,
    autoNumberValue,
  };
}

export default useQuestionFields;
